// Global Instructions Rule Applied!

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Select, Space, Table, Tag, Tooltip, Typography } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import {
  fetchIndustryAccounts,
  updateAccountIndustry,
  fetchIndustries,
  type IndustryAccountRow
} from '../services/configurationService.ts'

const { Title, Text } = Typography

interface IndustryAccountsPanelProps {
  industryId: string
  industryName: string
  onBack: () => void
}

interface Row extends IndustryAccountRow {
  _changed?: boolean
}

// Detail screen for one industry: the accounts categorised with it, alphabetical,
// each with its role(s) and an editable industry. When an account's industry is
// changed it no longer matches this screen's filter, but it stays visible (flagged
// "changed") so the user can see exactly what was reassigned.
export default function IndustryAccountsPanel({ industryId, industryName, onBack }: IndustryAccountsPanelProps) {
  const [rows, setRows] = useState<Row[]>([])
  const [loading, setLoading] = useState(false)
  const [industryOptions, setIndustryOptions] = useState<{ value: string; label: string }[]>([])
  const [savingId, setSavingId] = useState<string | null>(null)

  const token = () => getAccessToken(store.getState().auth)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.all([
      fetchIndustryAccounts(industryId, token()),
      fetchIndustries([], [], 1, 1000, token())
    ])
      .then(([acc, inds]) => {
        if (cancelled) return
        if (acc.error) showErrorToast(acc.error)
        setRows(acc.data)
        setIndustryOptions((inds.data || []).map((i) => ({ value: i.industries__id, label: i.industries__name })))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [industryId])

  const handleChangeIndustry = useCallback(
    async (row: Row, newIndustryId: string) => {
      if (!newIndustryId || newIndustryId === row.accounts__industry) return
      setSavingId(row.accounts__id)
      try {
        const res = await updateAccountIndustry(row.accounts__id, newIndustryId, token())
        if (!res.success) {
          showErrorToast(res.error || 'Failed to update industry')
          return
        }
        const newLabel = industryOptions.find((o) => o.value === newIndustryId)?.label || ''
        // Keep the row visible (it no longer matches this industry) and flag it.
        setRows((prev) =>
          prev.map((r) =>
            r.accounts__id === row.accounts__id
              ? { ...r, accounts__industry: newIndustryId, accounts__industry__label: newLabel, _changed: true }
              : r
          )
        )
        toast.success(`${row.accounts__name} → ${newLabel}`)
      } finally {
        setSavingId(null)
      }
    },
    [industryOptions]
  )

  const columns: ColumnsType<Row> = useMemo(
    () => [
      {
        title: 'Account',
        dataIndex: 'accounts__name',
        key: 'accounts__name',
        defaultSortOrder: 'ascend',
        sorter: (a, b) => (a.accounts__name || '').localeCompare(b.accounts__name || ''),
        render: (name: string, record: Row) => (
          <Space>
            <span className='font-medium'>{name}</span>
            {record._changed && <Tag color='gold'>changed</Tag>}
          </Space>
        )
      },
      {
        title: 'Role(s)',
        dataIndex: 'accounts__roles_label',
        key: 'accounts__roles_label',
        render: (v: string) => (v ? v : <Text type='secondary'>—</Text>)
      },
      {
        title: 'Industry',
        dataIndex: 'accounts__industry',
        key: 'accounts__industry',
        width: 280,
        render: (_: unknown, record: Row) => (
          <Select
            showSearch
            value={record.accounts__industry || undefined}
            options={industryOptions}
            optionFilterProp='label'
            style={{ width: 240 }}
            loading={savingId === record.accounts__id}
            onChange={(val) => handleChangeIndustry(record, val)}
          />
        )
      }
    ],
    [industryOptions, savingId, handleChangeIndustry]
  )

  return (
    <div className='w-full h-full flex flex-col overflow-hidden'>
      <div className='flex items-center gap-3 mb-3'>
        <Tooltip title='Back to Industries'>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack} aria-label='Back to Industries' />
        </Tooltip>
        <Title level={4} style={{ margin: 0 }}>
          {industryName} — Accounts
        </Title>
      </div>

      <div className='flex-1 min-h-0 overflow-auto'>
        <Table<Row>
          rowKey='accounts__id'
          columns={columns}
          dataSource={rows}
          loading={loading}
          pagination={false}
          size='middle'
          onRow={(record) => (record._changed ? { style: { backgroundColor: '#fffbe6' } } : {})}
        />
      </div>
    </div>
  )
}
